import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { 
  Mail, 
  MessageSquare, 
  Phone, 
  Users, 
  TrendingUp, 
  AlertCircle,
  Send,
  Calendar,
  Settings,
  BarChart3
} from 'lucide-react';
import { communicationService } from '../../services/api';
import { CommunicationStats } from '../../types';

export default function CommunicationDashboard() {
  const [activeTab, setActiveTab] = useState<'dashboard' | 'templates' | 'logs' | 'preferences'>('dashboard');

  // Statistiques
  const { data: stats, isLoading: statsLoading } = useQuery<CommunicationStats>({
    queryKey: ['communication-stats'],
    queryFn: () => communicationService.getStats().then(res => res.data)
  });

  // Logs récents
  const { data: recentLogs } = useQuery({
    queryKey: ['recent-logs'],
    queryFn: () => communicationService.getRecentLogs().then(res => res.data)
  });

  const statCards = [
    {
      title: 'Messages totaux',
      value: stats?.total_messages || 0,
      icon: MessageSquare,
      color: 'bg-blue-500',
      bgColor: 'bg-blue-50',
      textColor: 'text-blue-700'
    },
    {
      title: 'Emails envoyés',
      value: stats?.emails_sent || 0,
      icon: Mail,
      color: 'bg-green-500',
      bgColor: 'bg-green-50',
      textColor: 'text-green-700'
    },
    {
      title: 'SMS envoyés',
      value: stats?.sms_sent || 0,
      icon: Phone,
      color: 'bg-purple-500',
      bgColor: 'bg-purple-50',
      textColor: 'text-purple-700'
    },
    {
      title: 'Taux de succès',
      value: `${stats?.success_rate || 0}%`,
      icon: TrendingUp,
      color: 'bg-emerald-500',
      bgColor: 'bg-emerald-50',
      textColor: 'text-emerald-700'
    }
  ];

  const quickActions = [
    {
      title: 'Envoyer un message',
      description: 'Envoyer un message à un ou plusieurs patients',
      icon: Send,
      color: 'bg-teal-600 hover:bg-teal-700',
      action: () => setActiveTab('logs') // Pour l'instant
    },
    {
      title: 'Rappels de RDV',
      description: 'Envoyer les rappels de rendez-vous de demain',
      icon: Calendar,
      color: 'bg-green-500 hover:bg-green-600',
      action: () => handleSendReminders()
    },
    {
      title: 'Gérer les templates',
      description: 'Créer et modifier les modèles de messages',
      icon: Settings,
      color: 'bg-purple-500 hover:bg-purple-600',
      action: () => setActiveTab('templates')
    },
    {
      title: 'Voir les statistiques',
      description: 'Consulter les rapports détaillés',
      icon: BarChart3,
      color: 'bg-orange-500 hover:bg-orange-600',
      action: () => setActiveTab('logs')
    }
  ];

  const handleSendReminders = async () => {
    try {
      const response = await communicationService.sendAppointmentReminders();
      alert(response.data.message);
    } catch (error) {
      console.error('Erreur envoi rappels:', error);
      alert('Erreur lors de l\'envoi des rappels');
    }
  };

  if (statsLoading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Communications</h1>
          <p className="text-gray-600 mt-1">
            Gérez les communications avec vos patients
          </p>
        </div>
      </div>

      {/* Navigation des onglets */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {[
            { key: 'dashboard', label: 'Tableau de bord', icon: BarChart3 },
            { key: 'templates', label: 'Templates', icon: Settings },
            { key: 'logs', label: 'Historique', icon: MessageSquare },
            { key: 'preferences', label: 'Préférences', icon: Users }
          ].map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key as any)}
                className={`flex items-center py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === tab.key
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Icon className="h-4 w-4 mr-2" />
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Contenu selon l'onglet actif */}
      {activeTab === 'dashboard' && (
        <div className="space-y-6">
          {/* Cartes de statistiques */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {statCards.map((card, index) => {
              const Icon = card.icon;
              return (
                <div key={index} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                  <div className="flex items-center">
                    <div className={`p-3 rounded-lg ${card.bgColor}`}>
                      <Icon className={`h-6 w-6 ${card.textColor}`} />
                    </div>
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-600">{card.title}</p>
                      <p className="text-2xl font-bold text-gray-900">{card.value}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Actions rapides */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Actions rapides</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {quickActions.map((action, index) => {
                const Icon = action.icon;
                return (
                  <button
                    key={index}
                    onClick={action.action}
                    className={`p-4 rounded-lg text-white text-left transition-colors ${action.color}`}
                  >
                    <Icon className="h-6 w-6 mb-2" />
                    <h3 className="font-medium mb-1">{action.title}</h3>
                    <p className="text-sm opacity-90">{action.description}</p>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Messages récents */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Messages récents</h2>
            </div>
            <div className="divide-y divide-gray-200">
              {recentLogs && recentLogs.length > 0 ? (
                recentLogs.slice(0, 5).map((log: any) => (
                  <div key={log.id} className="p-6 flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div className={`p-2 rounded-lg ${
                        log.communication_type === 'email' ? 'bg-blue-50' :
                        log.communication_type === 'sms' ? 'bg-purple-50' :
                        'bg-green-50'
                      }`}>
                        {log.communication_type === 'email' ? (
                          <Mail className="h-4 w-4 text-blue-600" />
                        ) : log.communication_type === 'sms' ? (
                          <Phone className="h-4 w-4 text-purple-600" />
                        ) : (
                          <MessageSquare className="h-4 w-4 text-green-600" />
                        )}
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">
                          {log.patient_details?.nom} {log.patient_details?.prenoms}
                        </p>
                        <p className="text-sm text-gray-500">{log.subject}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        log.status === 'sent' ? 'bg-green-100 text-green-800' :
                        log.status === 'failed' ? 'bg-red-100 text-red-800' :
                        'bg-yellow-100 text-yellow-800'
                      }`}>
                        {log.status_display}
                      </span>
                      <p className="text-xs text-gray-500 mt-1">
                        {new Date(log.created_at).toLocaleDateString('fr-FR')}
                      </p>
                    </div>
                  </div>
                ))
              ) : (
                <div className="p-6 text-center text-gray-500">
                  <MessageSquare className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                  <p>Aucun message récent</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Messages d'information pour les autres onglets */}
      {activeTab !== 'dashboard' && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
          <AlertCircle className="h-12 w-12 mx-auto mb-4 text-gray-400" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {activeTab === 'templates' && 'Gestion des Templates'}
            {activeTab === 'logs' && 'Historique des Communications'}
            {activeTab === 'preferences' && 'Préférences des Patients'}
          </h3>
          <p className="text-gray-600 mb-4">
            {activeTab === 'templates' && 'Cette section permettra de créer et gérer les modèles de messages.'}
            {activeTab === 'logs' && 'Cette section affichera l\'historique complet des communications.'}
            {activeTab === 'preferences' && 'Cette section permettra de gérer les préférences de notification.'}
          </p>
          <p className="text-sm text-gray-500">
            Interface en cours de développement...
          </p>
        </div>
      )}
    </div>
  );
}